"use client";

import type { HeroIntroductionBlock } from "@mw/shared/payload-types";
import type { FC } from "react";

import { ModalVideoPlayer } from "@components/core/ModalVideoPlayer";
import { Button } from "@ui/components/core/Button";
import Quote from "@ui/components/core/Quote";
import Play20 from "@ui/icons/20x20/play-20.svg";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { getLinkProps, getVideoUrl } from "@utilities/payload";

import { Block } from "./Block";
import clsx from "clsx";
import { Link } from "@ui/components/core/Link";
import { pxToRem } from "@ui/lib/utils";
import { BlockTitle } from "./BlockTitle";

/**
 * @deprecated Use BlockTitle instead.
 */
export const BlockHeroIntroduction: FC<Omit<HeroIntroductionBlock, "blockType">> = ({
  prefix,
  title: titleV1,
  titleV2,
  description,
  introductionVideo,
  quote,
  useNewDesign = false,
  centered = false,
  link,
  secondaryLink,
}) => {
  const title = titleV2?.text || titleV1;
  if (!title) {
    return null;
  }

  const hasQuote = !!quote?.text;
  const hasVideo = !!getVideoUrl(introductionVideo?.videoFile);

  if (!hasQuote && !hasVideo) {
    return (
      <BlockTitle
        layout={centered ? "default" : "two-column"}
        titleAlignment={centered ? "center" : undefined}
        titleComplex={{ text: title, options: { tag: titleV2?.options?.tag, size: titleV2?.options?.size } }}
        preTitleComplex={{ text: prefix, options: { tag: "span" } }}
        subtitleComplex={{ text: description }}
        primaryLink={link}
        secondaryLink={secondaryLink}
      />
    );
  }

  if (useNewDesign) {
    const Heading = titleV2?.options?.tag || "h2";
    const size = titleV2?.options?.size || "medium";
    const capHeight = size === "large" ? 34.5 : 26;
    const primaryLinkProps = getLinkProps(link);
    const secondaryLinkProps = getLinkProps(secondaryLink);
    const videoButtonVariant = !!primaryLinkProps ? "elevated" : "primary";
    // Don't show video if all three (primary, secondary, and video) are present
    const hasTwoLinks = !!primaryLinkProps && !!secondaryLinkProps;
    const showVideo = hasVideo && !hasTwoLinks;

    const buttons = (
      <div className={clsx("tw-flex tw-flex-wrap tw-gap-2", centered && "tw-justify-center")}>
        {!!primaryLinkProps && (
          <Link {...primaryLinkProps} asButton buttonVariants={{ size: "lg" }}>
            {primaryLinkProps.children}
          </Link>
        )}
        {showVideo && (
          <ModalVideoPlayer
            customTrigger={
              <Button icon={<Play20 />} iconPosition={ICON_ALIGN_OPTIONS.BEFORE} size='lg' variant={videoButtonVariant}>
                {introductionVideo?.buttonText}
              </Button>
            }
            label={introductionVideo?.buttonText || ""}
            video={getVideoUrl(introductionVideo?.videoFile)}
          />
        )}
        {!!secondaryLinkProps && (
          <Link {...secondaryLinkProps} asButton buttonVariants={{ size: "lg", variant: "elevated" }}>
            {secondaryLinkProps.children}
          </Link>
        )}
      </div>
    );

    const hasButtons = !!primaryLinkProps || showVideo || !!secondaryLinkProps;

    return (
      <div className={clsx(centered && "tw-text-center")}>
        {prefix && <div className='tw-type-sm tw-text-gray-500 tw-font-medium tw-mb-7'>{prefix}</div>}
        <div className={clsx(!centered && "tw-flex tw-flex-col lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-x-12")}>
          <div className='tw-contents lg:tw-block'>
            <Heading
              className={clsx(
                "tw-text-black tw-text-balance",
                size === "large" && "tw-type-6xl",
                size === "medium" && "tw-type-5xl",
                centered ? "tw-max-w-lg tw-mx-auto" : "tw-max-w-lg lg:tw-max-w-none",
              )}
            >
              {title}
            </Heading>
            {hasButtons && description && !centered && (
              <div className='tw-mt-8 tw-order-last lg:tw-order-none'>{buttons}</div>
            )}
          </div>
          {description && (
            <div
              className={clsx(
                "tw-flex tw-flex-col",
                centered ? "tw-max-w-2xl tw-mx-auto tw-mt-8" : "tw-max-w-2xl tw-mt-8 lg:tw-mt-0 lg:tw-max-w-none",
              )}
            >
              <p className={clsx("tw-text-pretty tw-mt-auto", size === "large" ? "tw-type-xl" : "tw-type-base")}>
                {description}
              </p>
              {/* Spacer equal to the height of the button (48px) plus its top margin (32px) */}
              {hasButtons && !centered && (
                <div className='tw-h-0 tw-flex-auto' style={{ maxHeight: pxToRem(48 + 32) }} />
              )}
            </div>
          )}
          {hasButtons && !description && !centered && (
            <div className='tw-relative tw-mt-8 lg:tw-mt-0'>
              {/* On desktop the button is positioned so that it's centered-aligned with the title when
              there's only one line, and aligned to the baseline when there's more than one line.
              Additionally, the button is absolutely positioned so it doesn't affect the height of the block. */}
              <div
                className='lg:tw-absolute lg:tw-right-0'
                // max((buttonHeight - titleCapHeight) / -2, 100% - buttonHeight)
                style={{ top: `max(${pxToRem((48 - capHeight) / -2)}, calc(100% - ${pxToRem(48)}))` }}
              >
                {buttons}
              </div>
            </div>
          )}
          {hasButtons && centered && <div className='tw-mt-8'>{buttons}</div>}
        </div>
      </div>
    );
  }

  return (
    <Block name='hero-introduction'>
      <section className='container flex flex-col gap-64 pb-140 pt-100 lg:flex-row lg:py-200'>
        {/* Left column */}
        <div className='flex-1'>
          {prefix && <div className='f-heading-04 text-light'>{prefix}</div>}
          <h2 className='f-heading-01 mb-32 max-w-[360px]'>{title}</h2>
          <ModalVideoPlayer
            customTrigger={
              <Button icon={<Play20 />} iconPosition={ICON_ALIGN_OPTIONS.BEFORE} size='lg' variant='secondary'>
                {introductionVideo?.buttonText}
              </Button>
            }
            label={introductionVideo?.buttonText || ""}
            video={getVideoUrl(introductionVideo?.videoFile)}
          />
        </div>
        {/* Right column */}
        <div className={clsx("flex-1", prefix && "md:pt-32")}>
          <p className='f-heading-04 mb-40 text-balance'>{description}</p>
          {quote && quote.text && <Quote author={quote.author} quote={quote.text} title={quote.title} />}
        </div>
      </section>
    </Block>
  );
};
